/**
 * IVTS supplier stock-check client.
 *
 * Reads supplier metafields from a <div data-supplier-stock> element rendered
 * by snippet supplier-stock-check.liquid. Watches the product page quantity +
 * variant inputs and, after a short idle window, POSTs to the stock bot to
 * confirm the requested combination is available at the supplier.
 *
 * The Add-to-Cart button is enabled/disabled based on the result. On bot
 * failure or timeout, we DO NOT block the sale (sales > stock check accuracy).
 *
 * IMPORTANT — this theme renders the variant pickers inside <variant-selects>
 * and the quantity stepper inside <quantity-selector>, BOTH outside the
 * <form action="/cart/add"> (they are linked via the `form` attribute). So we
 * must query them at the document level, not under the form, and listen for
 * events at the document level too.
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-supplier-stock]');
  if (!root) return;

  const supplier = root.dataset.supplier;
  const sourceUrl = root.dataset.sourceUrl;
  const botUrl = root.dataset.botUrl;
  const timestamp = root.dataset.timestamp;
  const signature = root.dataset.signature;
  const debounceMs = parseInt(root.dataset.debounceMs, 10) || 2000;
  // PRO customers may oversell up to supplier stock; B2C/anonymous stay limited
  // to IVTS's own Shopify inventory (these variants are CONTINUE, so Shopify
  // won't enforce that limit — we do it here).
  const isPro = root.dataset.isPro === 'true';

  if (!supplier || !sourceUrl || !botUrl || !timestamp || !signature) return;

  const textEl = root.querySelector('[data-supplier-stock-text]');

  // Per-variant IVTS Shopify inventory: { id: {qty, tracked} }
  let inventoryById = {};
  try {
    const invEl = document.querySelector('[data-supplier-inventory]');
    if (invEl) {
      JSON.parse(invEl.textContent || '[]').forEach((r) => {
        inventoryById[String(r.id)] = r;
      });
    }
  } catch (e) { /* ignore */ }

  // The add-to-cart button(s): main + optional sticky bar. They may be rendered
  // disabled + .supplier-gated server-side (default blocked); we lift the lock
  // only after the bot validates.
  const form = document.querySelector('form[action$="/cart/add"]');
  function addButtons() {
    const set = new Set();
    document.querySelectorAll('button[name="add"], #AddToCart, #AddToCartSticky').forEach((b) => set.add(b));
    if (form) form.querySelectorAll('[type="submit"]').forEach((b) => set.add(b));
    return Array.from(set);
  }

  let debounceTimer = null;
  let lastCheckId = 0;
  let lastEffectiveMax = null; // max orderable for the current variant/context (null = unknown)
  let botBlocked = false;      // supplier rupture / IVTS 0 stock → never sellable
  let qtyInvalid = false;      // requested qty exceeds the max → must be corrected
  let validated = false;       // false until the bot has resolved for the current variant
                               // (blocks add-to-cart during the check window; closes the
                               //  gap where a shopper could buy before validation)
  const CHECK_TIMEOUT_MS = 8000; // fail-open if the bot doesn't answer within this
  let pillBase = { state: 'idle', msg: '' }; // base pill state from the last bot check

  function setStatus(state, message) {
    if (!textEl) return;
    root.dataset.state = state;
    textEl.textContent = message;
  }

  // Map a theme option name (often French: "Taille de l'accessoire", "Couleur")
  // to the canonical key the bot's matcher understands (size / color).
  function canonOptName(raw) {
    let n = String(raw || '').replace(/--sticky$/i, '').trim();
    const lc = n.toLowerCase();
    if (/taille|pointure|size|größe/.test(lc)) return 'size';
    if (/couleur|coloris|colour|color|farbe/.test(lc)) return 'color';
    return lc;
  }

  function gatherVariantOptions() {
    const options = {};

    // Scope to <variant-selects> blocks (this theme). Fall back to whole doc.
    const blocks = document.querySelectorAll('variant-selects');
    const scopes = blocks.length ? Array.from(blocks) : [document];

    scopes.forEach((scope) => {
      // Dropdown picker: <select name="options[Taille de l'accessoire]">
      scope.querySelectorAll('select[name^="options["]').forEach((sel) => {
        const m = /options\[(.+?)\]/.exec(sel.name || '');
        if (m && sel.value) options[canonOptName(m[1])] = sel.value;
      });
      // Button / color picker: checked radios named after the option.
      // Skip line-item PROPERTIES (name="properties[…]" — flocage, emplacement,
      // couleur perso, etc.): they are NOT variant options and must not be sent
      // to the bot (would cause variant_not_found → fail-open → no cap).
      scope.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
        const nm = r.name;
        if (!nm || nm === 'id' || nm === 'quantity' || /^properties\[/i.test(nm) || /^options\[/.test(nm)) {
          if (/^options\[/.test(nm)) {
            const m = /options\[(.+?)\]/.exec(nm);
            if (m && r.value) options[canonOptName(m[1])] = r.value;
          }
          return;
        }
        if (r.value) options[canonOptName(nm)] = r.value;
      });
    });

    // Dawn-style data attributes (other themes).
    if (!Object.keys(options).length) {
      document.querySelectorAll('[data-option-name][data-option-value]').forEach((el) => {
        if (el.checked || el.selected) {
          options[canonOptName(el.dataset.optionName)] = el.dataset.optionValue;
        }
      });
    }

    // Last resort: a single combined select (name="id") with "S / Noir" text.
    if (!Object.keys(options).length) {
      const vs = document.querySelector('select[name="id"]');
      if (vs && vs.selectedOptions[0]) {
        const parts = (vs.selectedOptions[0].textContent || '')
          .split(/\s*\/\s*/).map((s) => s.trim()).filter(Boolean);
        if (parts[0]) options.size = parts[0];
        if (parts[1]) options.color = parts[1];
      }
    }

    return options;
  }

  // All quantity inputs for this product (main + optional sticky bar). They are
  // kept in sync by the theme, so reading the first is fine; we clamp them all.
  function qtyInputs() {
    const set = new Set();
    document.querySelectorAll('quantity-selector input[name="quantity"], input[name="quantity"]')
      .forEach((el) => set.add(el));
    return Array.from(set);
  }

  function gatherQuantity() {
    for (const el of qtyInputs()) {
      const v = parseInt(el.value, 10);
      if (Number.isFinite(v) && v > 0) return v;
    }
    return 1;
  }

  // Currently-selected variant id (the theme keeps a hidden [name="id"] in sync).
  function selectedVariantId() {
    const el =
      (form && form.querySelector('[name="id"]')) ||
      document.querySelector('variant-selects [name="id"], [name="id"]');
    return el ? String(el.value) : null;
  }

  // IVTS's own Shopify on-hand for the selected variant. Returns a number when
  // the variant is inventory-tracked, else null (no Shopify limit to enforce).
  function shopifyMaxForSelected() {
    const id = selectedVariantId();
    if (!id) return null;
    const rec = inventoryById[id];
    if (!rec || !rec.tracked) return null;
    return typeof rec.qty === 'number' ? Math.max(rec.qty, 0) : null;
  }

  // B2C landing fix: these dropship variants are CONTINUE, so Shopify treats them
  // all as "available" and defaults to the FIRST one — which may have 0 IVTS stock
  // → particulier lands on a "Rupture". If the shopper hasn't picked a variant,
  // auto-select the first variant that actually has IVTS stock (B2C/anonymous only).
  // Returns true if it switched the selection.
  function selectVariantOptions(values) {
    const scope = document.querySelector('variant-selects') || document;
    let changed = false;
    values.forEach((val) => {
      if (val == null) return;
      const sval = String(val);
      const radio = scope.querySelector(`input[type="radio"][value="${(window.CSS && CSS.escape) ? CSS.escape(sval) : sval}"]`);
      if (radio) {
        if (!radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); changed = true; }
        return;
      }
      scope.querySelectorAll('select[name^="options["]').forEach((sel) => {
        if ([...sel.options].some((o) => o.value === sval) && sel.value !== sval) {
          sel.value = sval; sel.dispatchEvent(new Event('change', { bubbles: true })); changed = true;
        }
      });
    });
    return changed;
  }

  function autoSelectInStockVariant() {
    if (isPro) return false;                                  // B2C / anonymous only
    try { if (new URL(location.href).searchParams.get('variant')) return false; } catch (e) {}
    const curId = selectedVariantId();
    const cur = curId && inventoryById[curId];
    if (!cur || !cur.tracked || cur.qty > 0) return false;    // current default already in stock (or untracked)
    const inStockId = Object.keys(inventoryById).find((id) => inventoryById[id].tracked && inventoryById[id].qty > 0);
    if (!inStockId || inStockId === curId) return false;       // none in stock (or already it)
    const vsJson = document.querySelector('variant-selects script[type="application/json"]');
    if (!vsJson) return false;
    let variants; try { variants = JSON.parse(vsJson.textContent); } catch (e) { return false; }
    const target = (variants || []).find((v) => String(v.id) === String(inStockId));
    if (!target) return false;
    return selectVariantOptions([target.option1, target.option2, target.option3]);
  }

  // Set the native max attribute on the quantity inputs (bounds the +/- steppers)
  // WITHOUT changing the current value — we don't auto-correct, we block.
  function markMax(recMax) {
    qtyInputs().forEach((el) => {
      if (recMax !== null && recMax >= 0) el.setAttribute('max', String(recMax));
    });
  }

  // Build / position the validation tooltip on the quantity field. Styles are
  // inline so it renders correctly regardless of external CSS caching.
  function ensureTip(qs) {
    let tip = qs.querySelector(':scope > .qty-max-tip');
    if (tip) return tip;
    tip = document.createElement('div');
    tip.className = 'qty-max-tip';
    tip.innerHTML = '<span class="qmt-ico" aria-hidden="true">!</span><span class="qmt-arrow"></span><span class="qmt-txt"></span>';
    Object.assign(tip.style, {
      position: 'absolute', top: 'calc(100% + 9px)', left: '0', zIndex: '60',
      display: 'flex', alignItems: 'center', gap: '8px',
      width: 'max-content', maxWidth: 'min(280px, 80vw)', minWidth: '210px',
      padding: '9px 13px', background: '#fff', border: '1px solid #e5e7eb',
      borderLeft: '4px solid #dc2626', borderRadius: '6px',
      boxShadow: '0 6px 18px rgba(0,0,0,.16)', fontSize: '13px', lineHeight: '1.35',
      color: '#1f2937', fontWeight: '500', whiteSpace: 'normal',
    });
    Object.assign(tip.querySelector('.qmt-ico').style, {
      flexShrink: '0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: '18px', height: '18px', borderRadius: '4px', background: '#f59e0b',
      color: '#fff', fontWeight: '800', fontSize: '12px', lineHeight: '1',
    });
    Object.assign(tip.querySelector('.qmt-arrow').style, {
      position: 'absolute', top: '-6px', left: '16px', width: '11px', height: '11px',
      background: '#fff', borderLeft: '1px solid #e5e7eb', borderTop: '1px solid #e5e7eb',
      transform: 'rotate(45deg)',
    });
    qs.appendChild(tip);
    return tip;
  }

  // Show/hide the "max quantity is N" tooltip on every quantity field.
  function showTip(recMax) {
    const msg = (recMax !== null && recMax >= 1) ? `La quantité maximum disponible est de ${recMax}.` : '';
    document.querySelectorAll('quantity-selector').forEach((qs) => {
      const inp = qs.querySelector('input[name="quantity"]');
      if (inp) { if (msg) inp.title = msg; else inp.removeAttribute('title'); }
      if (getComputedStyle(qs).position === 'static') qs.style.position = 'relative';
      const existing = qs.querySelector(':scope > .qty-max-tip');
      if (msg) {
        const tip = existing || ensureTip(qs);
        tip.querySelector('.qmt-txt').textContent = msg;
      } else if (existing) {
        existing.remove();
      }
    });
  }

  // Gray out / re-enable the Add-to-cart button based on block reasons.
  // While the bot hasn't resolved for the current variant (!validated), the
  // button stays disabled — closing the gap where a shopper could add to cart
  // before the stock check completes.
  function updateAddButton() {
    const blocked = !validated || botBlocked || qtyInvalid;
    addButtons().forEach((b) => {
      b.disabled = blocked;
      b.classList.toggle('supplier-gated', blocked); // drives the CSS hard-lock
      b.style.opacity = blocked ? '0.5' : '';
      b.style.cursor = blocked ? (validated ? 'not-allowed' : 'progress') : '';
      b.style.pointerEvents = blocked ? 'none' : '';
      if (!blocked) b.removeAttribute('data-supplier-gated');
    });
  }

  // The supplier pill mirrors the qty state: when the requested quantity is over
  // the max, the (otherwise green) "En stock" pill turns orange and tells the
  // shopper to lower the quantity; it returns to its base state once back in range.
  function refreshPill() {
    if (qtyInvalid && (pillBase.state === 'ok' || pillBase.state === 'low')) {
      setStatus('max', '⚠ Restez dans la limite de quantité pour commander');
    } else {
      setStatus(pillBase.state, pillBase.msg);
    }
  }

  // Compare the requested qty to the effective max; show tooltip + block add when
  // over. Does NOT modify the quantity — the shopper corrects it themselves.
  function applyQtyValidation() {
    const cap = lastEffectiveMax;
    const q = gatherQuantity();
    qtyInvalid = cap !== null && cap >= 1 && q > cap;
    // Once the bot has resolved, remember this variant's result so quantity
    // changes (and re-selecting it later) don't trigger another bot call.
    if (validated) variantCache[variantKey()] = { lastEffectiveMax, botBlocked, pillBase };
    showTip(qtyInvalid ? cap : null);
    refreshPill();
    updateAddButton();
  }

  // Per-variant cache of the bot result: we check a variant ONCE, then validate
  // quantity changes locally against the cached cap (the supplier max doesn't
  // depend on the requested qty). Re-selecting a known variant restores instantly.
  const variantCache = {};
  function variantKey() {
    const opts = gatherVariantOptions();
    if (Object.keys(opts).length) return 'opt:' + JSON.stringify(opts);
    const id = selectedVariantId();
    return id ? 'id:' + id : 'single';
  }

  async function checkStock() {
    const checkId = ++lastCheckId;
    const quantity = gatherQuantity();
    const variantOptions = gatherVariantOptions();

    setStatus('loading', (window.STOCK_TEXTS && window.STOCK_TEXTS.checking) || 'Vérification du stock…');

    try {
      const ctrl = new AbortController();
      const to = setTimeout(() => ctrl.abort(), CHECK_TIMEOUT_MS);
      let res;
      try {
        res = await fetch(botUrl, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            supplier,
            productUrl: sourceUrl,
            quantity,
            variantOptions,
            timestamp,
            signature,
          }),
          signal: ctrl.signal,
        });
      } finally {
        clearTimeout(to);
      }

      // If a newer request started after us, drop our response
      if (checkId !== lastCheckId) return;

      // We have a resolution for the current variant → unblock the guard.
      validated = true;

      if (!res.ok) {
        // Bot error — fail open (allow sale, no cap)
        pillBase = { state: 'error', msg: '' };
        botBlocked = false; lastEffectiveMax = null;
        applyQtyValidation();
        return;
      }
      const data = await res.json();

      // --- Supplier signal (from the bot) ---
      const recMax = typeof data.recommended_max_qty === 'number' ? data.recommended_max_qty : null;
      const partial = /partial_stock_(\d+)/.exec(data.reason || '');
      // How many units the supplier actually has (null = unknown / unbounded).
      let supplierMax = recMax;
      if (!data.available && partial) supplierMax = parseInt(partial[1], 10);
      const supplierUnknown = !data.available &&
        (data.reason === 'product_not_in_catalog' || data.reason === 'variant_not_found');
      // Any "unavailable" that is neither a partial cap nor an unknown/fail-open
      // reason counts as OUT OF STOCK at the supplier — covers out_of_stock,
      // oos_variation, oos_simple, btn_disabled, add_to_cart_disabled, rupture_visible…
      const supplierHardOOS = !data.available && !partial && !supplierUnknown;

      // --- IVTS Shopify on-hand (B2C limit) ---
      const shopMax = shopifyMaxForSelected();

      // Unknown supplier mapping with no local figure to fall back on → fail open
      // (don't block sales on a mapping we can't read).
      if (supplierUnknown && (isPro || shopMax === null)) {
        botBlocked = false; lastEffectiveMax = null;
        pillBase = { state: 'error', msg: '' };
        applyQtyValidation();
        return;
      }

      // --- Effective cap by context ---
      //   B2C (particulier) → IVTS LOCAL stock only (the supplier is irrelevant).
      //   PRO               → SUPPLIER stock + IVTS LOCAL stock (sum). An item out
      //                       of stock at the supplier but held locally stays
      //                       sellable to PRO up to the local qty; 100 local + 300
      //                       supplier ⇒ PRO can order 400.
      const ivtsAvail = (typeof shopMax === 'number') ? shopMax : 0;
      let effectiveMax;   // null = no cap (unbounded / fail-open)
      if (isPro) {
        const supAvail = supplierHardOOS ? 0 : supplierMax; // number, or null = unbounded
        effectiveMax = (supAvail === null) ? null : (supAvail + ivtsAvail);
      } else if (shopMax !== null) {
        effectiveMax = shopMax;            // B2C tracked → local stock only
      } else {
        effectiveMax = supplierHardOOS ? 0 : supplierMax; // B2C untracked → supplier signal
      }

      lastEffectiveMax = (typeof effectiveMax === 'number') ? effectiveMax : null;
      markMax(lastEffectiveMax);

      // Out of stock only when the governing cap is exactly 0.
      if (lastEffectiveMax === 0) {
        botBlocked = true;
        pillBase = { state: 'oos', msg: (window.STOCK_TEXTS && window.STOCK_TEXTS.outOfStock) || '✗ En rupture de stock' };
        applyQtyValidation();
        return;
      }

      // In stock — base pill; applyQtyValidation turns it orange if qty over cap.
      botBlocked = false;
      const low = !!data.low_stock && isPro;
      pillBase = low
        ? { state: 'low', msg: (window.STOCK_TEXTS && window.STOCK_TEXTS.low) || `⚠ Stock faible${lastEffectiveMax !== null ? ` (max ${lastEffectiveMax})` : ''}` }
        : { state: 'ok', msg: (window.STOCK_TEXTS && window.STOCK_TEXTS.ok) || '✓ En stock' };
      applyQtyValidation();
    } catch (err) {
      // Network failure / timeout / abort — fail open (don't block sales on a
      // bot outage), but mark validated so the guard releases the button.
      if (checkId !== lastCheckId) return;
      validated = true;
      pillBase = { state: 'error', msg: '' };
      botBlocked = false; lastEffectiveMax = null;
      applyQtyValidation();
    }
  }

  function scheduleCheck(delay) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(checkStock, typeof delay === 'number' ? delay : debounceMs);
  }

  // ---- Event wiring (document-level: inputs live outside the cart form) ----

  function isQtyTarget(t) {
    return t && (t.name === 'quantity' || (t.closest && t.closest('quantity-selector')));
  }
  function isVariantTarget(t) {
    if (!t) return false;
    if (t.name === 'id') return true;
    if (t.name && /^options\[/.test(t.name)) return true;
    if (t.dataset && t.dataset.optionName) return true;
    if (t.closest && t.closest('variant-selects')) return true;
    return false;
  }

  const QTY_BTN_SELECTOR =
    'quantity-selector .minus, quantity-selector .plus, quantity-selector button, ' +
    'quantity-input button, .quantity__button, .quantity-selector button, ' +
    '.qty-plus, .qty-minus, [name="plus"], [name="minus"], [data-quantity-button]';

  document.addEventListener('change', (e) => {
    if (isVariantTarget(e.target)) {
      const cached = variantCache[variantKey()];
      if (cached) {
        // Already checked this variant → restore instantly, NO bot call.
        lastEffectiveMax = cached.lastEffectiveMax;
        botBlocked = cached.botBlocked;
        pillBase = cached.pillBase;
        validated = true;
        qtyInvalid = false;
        applyQtyValidation();
      } else {
        // New variant → block until ONE automatic check resolves.
        lastEffectiveMax = null;
        qtyInvalid = false;
        validated = false;
        showTip(null);
        updateAddButton();
        scheduleCheck(300);     // single check on variant selection (short debounce)
      }
    } else if (isQtyTarget(e.target)) {
      applyQtyValidation();     // local validation only — no bot re-check on qty change
    }
  });

  document.addEventListener('input', (e) => {
    if (!isQtyTarget(e.target)) return;
    applyQtyValidation();       // local feedback while typing; no bot re-check, no auto-correct
  });

  // Quantity +/- buttons mutate the value via JS without firing input/change,
  // so revalidate shortly after the click.
  document.addEventListener('click', (e) => {
    if (e.target && e.target.closest && e.target.closest(QTY_BTN_SELECTOR)) {
      setTimeout(applyQtyValidation, 60);
    }
  });

  // Block adding to cart when: the bot hasn't validated yet (check in flight),
  // the quantity is over the max, or it's out of stock. The theme adds via JS,
  // so we intercept in the CAPTURE phase (before its handlers) on form submit,
  // Add-to-cart click, and Enter in the qty field. We do NOT correct the qty.
  function blockIfInvalid(e) {
    applyQtyValidation();
    if (!validated || qtyInvalid || botBlocked) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (!validated) {
        // Still checking — surface the in-progress state instead of a silent block.
        setStatus('loading', (window.STOCK_TEXTS && window.STOCK_TEXTS.checking) || 'Vérification du stock…');
      } else if (qtyInvalid) {
        const inp = (form && form.querySelector('input[name="quantity"]')) || document.querySelector('input[name="quantity"]');
        if (inp) inp.focus();
      }
      return false;
    }
  }
  if (form) form.addEventListener('submit', blockIfInvalid, true);
  addButtons().forEach((b) => b.addEventListener('click', blockIfInvalid, true));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && isQtyTarget(e.target)) blockIfInvalid(e);
  }, true);

  // Start with the button blocked. For B2C, switch to the first in-stock variant
  // if the default lands on a 0-stock one (that change triggers its own check);
  // otherwise auto-check IMMEDIATELY (no debounce) so an in-stock default unlocks fast.
  updateAddButton();
  if (!autoSelectInStockVariant()) checkStock();
})();
